import {
  BehaviorSubject,
  combineLatest,
  filter,
  from,
  map,
  switchMap,
} from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import { getCurrentProfile, profile$ } from "@/lib/profile";
import {
  boundsFromWeekRecord,
  currentWeekBounds,
  formatDateOnly,
  formatWeekRange,
  type WeekBounds,
  weekWithLabel,
} from "@/lib/week";
import type {
  BalanceSnapshotData,
  ChildWeekSummary,
  FamilyWeekSummary,
  JobLogData,
  JobLogRecord,
  JobRecord,
  Profile,
  WeekData,
  WeekRecord,
  WeekStatus,
} from "@/types/domain";
import { familyService } from "./family.service";

export interface ChildWeekAggregate {
  child: Profile;
  week: WeekRecord | null;
  logs: JobLogRecord[];
  status: WeekStatus;
  totalEarned: number;
  pendingCount: number;
}

export interface WeekRow {
  year: number;
  isoWeek: number;
  weekRangeLabel: string;
  lengthDays: number;
  startDate: string | null;
  endDate: string | null;
  children: ChildWeekAggregate[];
  totalPayout: number;
  jobCount: number;
  uniformStatus: WeekStatus | null;
}

interface ParentWeeksState {
  weeks: WeekRow[];
  currentWeek: FamilyWeekSummary | null;
  awaitingCloseCount: number;
  loading: boolean;
}

const state$ = new BehaviorSubject<ParentWeeksState>({
  weeks: [],
  currentWeek: null,
  awaitingCloseCount: 0,
  loading: true,
});

const parentAuth$ = profile$.pipe(
  filter((s) => !s.loading && s.profile?.data.role === "parent"),
);

const records$ = parentAuth$.pipe(
  switchMap((s) =>
    from(getDb()).pipe(
      switchMap((db) =>
        combineLatest([
          db.week_allowance.find({
            selector: { family_id: s.profile!.data.family_id, type: "week" },
          }).$,
          db.week_allowance.find({
            selector: {
              family_id: s.profile!.data.family_id,
              type: "job_log",
            },
          }).$,
        ]),
      ),
    ),
  ),
);

function buildAggregate(
  child: Profile,
  week: WeekRecord | null,
  allLogs: JobLogRecord[],
): ChildWeekAggregate {
  const logs = week ? allLogs.filter((l) => l.data.week_id === week.id) : [];
  const status: WeekStatus = week ? week.data.status : "open";
  return {
    child,
    week,
    logs,
    status,
    totalEarned: logs.reduce((s, l) => s + l.data.amount, 0),
    pendingCount: logs.filter(
      (l) => l.data.status === "pending" && l.data.requires_approval,
    ).length,
  };
}

combineLatest([records$, familyService.state$])
  .pipe(
    map(([[weekDocs, logDocs], family]) => {
      const weeks = weekDocs.map(
        (d) => d.toJSON() as RecordDoc as unknown as WeekRecord,
      );
      const allLogs = logDocs.map(
        (d) => d.toJSON() as RecordDoc as unknown as JobLogRecord,
      );
      const children = family.members.filter((m) => m.data.role === "child");

      // Group all (year, iso_week) tuples that have any week record.
      const keys = new Set<string>();
      for (const w of weeks) {
        keys.add(`${w.data.year}-${w.data.iso_week}`);
      }

      const rows: WeekRow[] = [];
      for (const key of keys) {
        const [yearStr, weekStr] = key.split("-");
        const year = Number(yearStr);
        const isoWeek = Number(weekStr);
        const aggregates: ChildWeekAggregate[] = children.map((child) => {
          const week =
            weeks.find(
              (w) =>
                w.user_id === child.id &&
                w.data.iso_week === isoWeek &&
                w.data.year === year,
            ) ?? null;
          return buildAggregate(child, week, allLogs);
        });
        const totalPayout = aggregates.reduce((s, c) => s + c.totalEarned, 0);
        const jobCount = aggregates.reduce((s, c) => s + c.logs.length, 0);
        const statuses = new Set(aggregates.map((a) => a.status));
        const uniformStatus =
          statuses.size === 1 ? (aggregates[0]?.status ?? null) : null;
        // Prefer the explicit start/end on any matching record; fall back to
        // legacy Mon–Sun synthesis from iso_week+year.
        const representative = aggregates.find((a) => a.week)?.week ?? null;
        const bounds = representative
          ? boundsFromWeekRecord(representative.data)
          : null;
        const weekRangeLabel = bounds
          ? formatWeekRange(bounds.start, bounds.end)
          : "";
        rows.push({
          year,
          isoWeek,
          weekRangeLabel,
          lengthDays: bounds?.length_days ?? 7,
          startDate: bounds ? formatDateOnly(bounds.start) : null,
          endDate: bounds ? formatDateOnly(bounds.end) : null,
          children: aggregates,
          totalPayout,
          jobCount,
          uniformStatus,
        });
      }
      rows.sort((a, b) =>
        a.year !== b.year ? b.year - a.year : b.isoWeek - a.isoWeek,
      );

      // Current-week summary used by ParentDashboard. "Current" is the week
      // that contains today under the family's offset.
      const offset = family.settings?.week_start_day ?? 1;
      const cur = currentWeekBounds(offset);
      const isoWeek = cur.iso_week;
      const year = cur.year;
      const today = new Date();
      const childSummaries: ChildWeekSummary[] = children
        .map((child) => {
          const week =
            weeks.find(
              (w) => w.user_id === child.id && weekContainsDate(w, today),
            ) ?? null;
          const logs = week
            ? allLogs.filter((l) => l.data.week_id === week.id)
            : [];
          return {
            child,
            week: week as WeekRecord,
            logs,
            totalEarned: logs.reduce((s, l) => s + l.data.amount, 0),
            pendingCount: logs.filter(
              (l) => l.data.status === "pending" && l.data.requires_approval,
            ).length,
          };
        })
        .filter((s) => s.week != null);

      const currentWeek: FamilyWeekSummary = {
        isoWeek,
        year,
        children: childSummaries,
        totalPayout: childSummaries.reduce((s, c) => s + c.totalEarned, 0),
        allConfirmed:
          childSummaries.length > 0 &&
          childSummaries.every((c) => c.week.data.status !== "open"),
      };

      const awaitingCloseCount = weeks.filter(
        (w) => w.data.status === "child_confirmed",
      ).length;

      return {
        weeks: rows,
        currentWeek,
        awaitingCloseCount,
        loading: false,
      } satisfies ParentWeeksState;
    }),
  )
  .subscribe((next) => state$.next(next));

async function patchRecordData(
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const db = await getDb();
  const doc = await db.week_allowance.findOne(id).exec();
  if (!doc) return;
  const json = doc.toJSON() as RecordDoc;
  const data = {
    ...(json.data as unknown as Record<string, unknown>),
    ...patch,
  };
  await doc.patch({
    data: data as unknown as Record<string, unknown>,
    updated_at: new Date().toISOString(),
  });
}

async function findExistingBalanceSnapshot(
  weekId: string,
): Promise<{ id: string } | null> {
  const db = await getDb();
  const docs = await db.week_allowance
    .find({ selector: { type: "balance_snapshot" } })
    .exec();
  for (const doc of docs) {
    const json = doc.toJSON() as RecordDoc;
    const data = json.data as unknown as BalanceSnapshotData;
    if (data.week_id === weekId) return { id: json.id };
  }
  return null;
}

function weekContainsDate(week: WeekRecord, date: Date): boolean {
  const dateStr = formatDateOnly(date);
  const start = week.data.start_date ?? null;
  const end = week.data.end_date ?? null;
  if (start && end) return start <= dateStr && dateStr <= end;
  const bounds = boundsFromWeekRecord(week.data);
  return (
    formatDateOnly(bounds.start) <= dateStr &&
    dateStr <= formatDateOnly(bounds.end)
  );
}

function buildWeekData(bounds: WeekBounds): WeekData {
  return {
    iso_week: bounds.iso_week,
    year: bounds.year,
    status: "open",
    week_range_label: formatWeekRange(bounds.start, bounds.end),
    start_date: formatDateOnly(bounds.start),
    end_date: formatDateOnly(bounds.end),
    start_day_offset: bounds.start_day_offset,
    child_confirmed_at: null,
    closed_at: null,
    approved_by: null,
    bank_balance_after: null,
    stock_value_after: null,
    total_invested: null,
    streak: 0,
    screen_time_hours: null,
    screen_time_amount: null,
  };
}

async function getOrCreateChildWeek(
  child: Profile,
  isoWeek: number,
  year: number,
): Promise<WeekRecord> {
  const db = await getDb();
  const docs = await db.week_allowance
    .find({
      selector: { family_id: child.data.family_id, type: "week" },
    })
    .exec();
  const labelled = docs
    .map((d) => d.toJSON() as RecordDoc as unknown as WeekRecord)
    .filter((w) => w.data.iso_week === isoWeek && w.data.year === year);
  const existing = labelled.find((w) => w.user_id === child.id);
  if (existing) return existing;

  // The child has no record for the requested week. Mirror bounds from any
  // family member's record with the same label — one exists for every row
  // reachable from the weeks list — so the log lands on the week being
  // viewed, never implicitly on the current week. Synthesize from the
  // family offset as a last resort.
  const sibling = labelled[0];
  const bounds = sibling
    ? boundsFromWeekRecord(sibling.data)
    : weekWithLabel(isoWeek, year, familyService.weekStartDay());
  const weekData = buildWeekData(bounds);
  const now = new Date().toISOString();
  const doc: RecordDoc = {
    id: crypto.randomUUID(),
    type: "week",
    family_id: child.data.family_id,
    user_id: child.id,
    created_at: now,
    updated_at: now,
    data: weekData as unknown as Record<string, unknown>,
  };
  await db.week_allowance.insert(doc);
  return doc as unknown as WeekRecord;
}

export const parentWeekService = {
  state$: state$.asObservable(),

  weeks$: state$.pipe(map((s) => s.weeks)),

  awaitingCloseCount$: state$.pipe(map((s) => s.awaitingCloseCount)),

  weekRow$(year: number, isoWeek: number) {
    return state$.pipe(
      map(
        (s) =>
          s.weeks.find((w) => w.year === year && w.isoWeek === isoWeek) ?? null,
      ),
    );
  },

  childWeeks$(childId: string) {
    return state$.pipe(
      map((s) => {
        const weeks: WeekRecord[] = [];
        for (const row of s.weeks) {
          for (const c of row.children) {
            if (c.child.id === childId && c.week) weeks.push(c.week);
          }
        }
        return weeks;
      }),
    );
  },

  async addLog(
    child: Profile,
    isoWeek: number,
    year: number,
    job: JobRecord,
    opts: {
      note?: string;
      amount?: number;
      loggedTime?: string;
      logDate?: string;
    } = {},
  ): Promise<void> {
    const week = await getOrCreateChildWeek(child, isoWeek, year);
    const amount = opts.amount ?? job.data.price ?? 0;

    const logData: JobLogData = {
      job_id: job.id,
      week_id: week.id,
      job_snapshot: {
        emoji: job.data.emoji,
        title: job.data.title,
        price: job.data.price,
        kind: job.data.kind,
      },
      amount,
      original_amount: amount,
      logged_time: opts.loggedTime ?? todayHHMM(),
      logged_date: opts.logDate ?? formatDateOnly(new Date()),
      status: "approved",
      note: opts.note ?? "",
      parent_note: "",
      requires_approval: false,
      screen_time_hours: null,
      band_label: null,
    };

    const db = await getDb();
    const now = new Date().toISOString();
    await db.week_allowance.insert({
      id: crypto.randomUUID(),
      type: "job_log",
      family_id: child.data.family_id,
      user_id: child.id,
      created_at: now,
      updated_at: now,
      data: logData as unknown as Record<string, unknown>,
    });
  },

  // Confirm on the child's behalf: the exact patch the child's own
  // confirmWeek applies. Creates the week for the viewed label first when
  // the child has no record (empty weeks can be confirmed and closed).
  async confirmChildWeek(
    child: Profile,
    isoWeek: number,
    year: number,
  ): Promise<void> {
    const week = await getOrCreateChildWeek(child, isoWeek, year);
    if (week.data.status !== "open") return;
    await patchRecordData(week.id, {
      status: "child_confirmed",
      child_confirmed_at: new Date().toISOString(),
    });
  },

  async deleteLog(logId: string): Promise<void> {
    const db = await getDb();
    const doc = await db.week_allowance.findOne(logId).exec();
    if (!doc) return;
    await doc.remove();
  },

  async closeChildWeek(
    weekId: string,
    snapshot: Omit<BalanceSnapshotData, "week_id" | "recorded_by">,
  ): Promise<void> {
    const { user, profile } = getCurrentProfile();
    if (!user || !profile) return;
    const db = await getDb();
    const weekDoc = await db.week_allowance.findOne(weekId).exec();
    if (!weekDoc) return;
    const week = weekDoc.toJSON() as RecordDoc as unknown as WeekRecord;

    const existing = await findExistingBalanceSnapshot(weekId);
    const now = new Date().toISOString();

    const snapshotData: BalanceSnapshotData = {
      ...snapshot,
      week_id: weekId,
      recorded_by: user.id,
    };

    if (existing) {
      await patchRecordData(
        existing.id,
        snapshotData as unknown as Record<string, unknown>,
      );
    } else {
      await db.week_allowance.insert({
        id: crypto.randomUUID(),
        type: "balance_snapshot",
        family_id: profile.data.family_id,
        user_id: week.user_id,
        created_at: now,
        updated_at: now,
        data: snapshotData as unknown as Record<string, unknown>,
      });
    }

    await patchRecordData(weekId, {
      status: "closed",
      closed_at: week.data.closed_at ?? now,
      approved_by: week.data.approved_by ?? user.id,
      bank_balance_after: snapshot.bank_balance,
      stock_value_after: snapshot.stock_value,
      total_invested: snapshot.total_invested,
    });
  },
};

function todayHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
